/**
 * multiSiteHexAlerts — the ALL-SITES alert board's HexAlertsApi.
 *
 * Same contract as the per-site adapter (hexAlerts.adapter), fanned out across every node the
 * operator can see: one camera roster, one merged alert feed, one set of counters. The shared
 * honeycomb then draws a panel per site and offers the Site filter it already knows how to
 * render — the board itself needs no notion of "cloud" or "multi-site" at all.
 *
 * ── Camera ids are SITE-QUALIFIED ──────────────────────────────────────────────────────────
 * The board keys everything (hex positions, counters, live tiles, name lookups) on the camera
 * id, and two nodes are two separate databases: nothing guarantees their ids don't collide, and
 * a collision would merge two different cameras into one hexagon. So every camera and every
 * alert crossing this adapter carries `nodeId::cameraId` (see `siteCameraKey`) instead of the
 * bare id.
 *
 * That key is also the ROUTING information the rest of the page needs: which node to open a
 * WebRTC subscription on for a preview, which node's tunnel to fetch a snapshot or a recording
 * through. HexLivePlayer and the page split it back apart with `splitSiteCameraKey`; nothing
 * else has to know the shape.
 *
 * ── One socket per site ────────────────────────────────────────────────────────────────────
 * /ws/node-events binds a session to exactly one nodeId, so watching N sites means N sockets
 * (each with its own snapshot + backoff, so one unreachable site can't stall the others). Each
 * one also holds that node's tunnel warm, which is what the board needs anyway: an alert that
 * only arrives once someone selects the site is not an all-sites board.
 *
 * The adapter — not the page — is the authority on what is currently active: every socket's
 * snapshot re-emits the MERGED feed, so a site reconnecting can't wipe the other sites' alerts
 * off the board.
 */
import type {
  HexAlert,
  HexAlertHandlers,
  HexAlertPage,
  HexAlertsApi,
  HexCamera,
} from '@trinetra/shared';
import {
  buildCounters,
  currentUser,
  getPreviewMeta,
  isActive,
  isOnline,
  mapAlert,
  rememberPreview,
  toHexCamera,
  toSeverity,
} from './hexAlerts.core';
import {
  getNodeCameras,
  getNodeRealtimeAlerts,
  getNodeAlerts,
  getNodeModelEventImage,
  acknowledgeNodeAlert,
  resolveNodeAlert,
  getStoredToken,
} from '../services/api.service';

const RECONNECT_BASE_MS = 3000;
const RECONNECT_MAX_MS = 30000;
// A link that stays open this long is considered healthy → reset the backoff.
const RECONNECT_STABLE_MS = 10000;

/** Most active alerts kept per site for the merged feed. The panel's card list is paged
 *  separately (listActiveAlerts); this buffer only has to cover the counters and the feed. */
const FEED_CAP_PER_SITE = 200;

/** Separates the node id from the camera id in a board camera key. Two colons: node ids are
 *  UUIDs today, but a single colon is common enough in identifiers to be worth avoiding. */
export const SITE_KEY_SEP = '::';

/** The board's id for a camera: which site, then which camera. */
export const siteCameraKey = (nodeId: string, cameraId: string) => `${nodeId}${SITE_KEY_SEP}${cameraId}`;

/**
 * Split a board camera key back into its site and camera.
 *
 * A key with no separator is a PLAIN camera id — that's what the per-site board passes — and
 * comes back with `nodeId: null`, meaning "whatever site is selected". Every consumer handles
 * both, so one player and one page serve both boards.
 */
export function splitSiteCameraKey(key: string): { nodeId: string | null; cameraId: string } {
  const at = key.indexOf(SITE_KEY_SEP);
  if (at < 0) return { nodeId: null, cameraId: key };
  return { nodeId: key.slice(0, at), cameraId: key.slice(at + SITE_KEY_SEP.length) };
}

/** One site this board covers. */
export interface SiteRef {
  nodeId: string;
  name: string;
}

/**
 * The all-sites api, plus the one question a page outside the board needs answered: which site
 * an alert id came from. Alert records are fetched by bare id down one site's tunnel, and the
 * board's alerts carry only a site-qualified CAMERA — this is the lookup the writes use too.
 */
export interface MultiSiteHexAlertsApi extends HexAlertsApi {
  siteOfAlertId(alertId: string): string | null;
}

/**
 * Build the all-sites HexAlertsApi over `sites`.
 *
 * Returns a NEW object each call, and the shared board re-runs its whole subscription when the
 * api identity changes — so callers must memoize on the site list (see AllSitesAlertBoardPage,
 * which keys on the node ids).
 */
export function createMultiSiteHexAlertsApi(sites: SiteRef[]): MultiSiteHexAlertsApi {
  /** alert id → the site it came from. Ack/resolve are handed an alert id and nothing else,
   *  but every write has to go down that site's tunnel. */
  const siteOfAlert = new Map<string, string>();

  /** Map a node's raw alert onto the board's shape: site-qualified camera, site remembered. */
  const mapSiteAlert = (nodeId: string, raw: any): HexAlert => {
    const alert = mapAlert(raw);
    if (alert.id) siteOfAlert.set(alert.id, nodeId);
    return { ...alert, cameraId: siteCameraKey(nodeId, alert.cameraId) };
  };

  /** Which site an alert belongs to — by its own id, else by its site-qualified camera. */
  const siteOf = (alert: HexAlert): string | null =>
    (alert.id && siteOfAlert.get(alert.id)) || splitSiteCameraKey(alert.cameraId || '').nodeId;

  return {
    async loadCameras(): Promise<HexCamera[]> {
      // Per site, and a site that fails is simply absent rather than fatal: one unreachable
      // node must not blank the whole estate's board. It reappears on the next load — the
      // events socket below refetches when its link comes back.
      const perSite = await Promise.all(
        sites.map(async (site) => {
          try {
            const raw: any = await getNodeCameras(site.nodeId);
            const cams: any[] = Array.isArray(raw) ? raw : raw?.data || raw?.cameras || [];
            return cams.map((c) => ({
              ...toHexCamera(c, site.nodeId, site.name),
              id: siteCameraKey(site.nodeId, c.id),
            }));
          } catch {
            return [] as HexCamera[];
          }
        }),
      );
      return perSite.flat();
    },

    watch({ onSnapshot, onAlert, onAlertPreview, onLifecycle, onCleared, onLiveStatus, onRecordingStatus }: HexAlertHandlers) {
      let cancelled = false;

      // ── The merged feed ─────────────────────────────────────────────────────
      // Held per site so one site's snapshot replaces only ITS OWN alerts, then re-emitted
      // whole. Emitting just the site's own slice would hand the page a feed containing one
      // site, which is how a reconnect would silently clear every other site's hexagons.
      const feeds = new Map<string, HexAlert[]>();

      const emit = () => {
        if (cancelled) return;
        const merged = [...feeds.values()].flat().sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        onSnapshot(buildCounters(merged), merged);
      };

      const setFeed = (nodeId: string, alerts: HexAlert[]) => {
        feeds.set(nodeId, alerts.slice(0, FEED_CAP_PER_SITE));
        emit();
      };

      const addToFeed = (nodeId: string, alert: HexAlert) => {
        const cur = feeds.get(nodeId) || [];
        feeds.set(
          nodeId,
          [alert, ...cur.filter((a) => !alert.id || a.id !== alert.id)].slice(0, FEED_CAP_PER_SITE),
        );
      };

      const dropFromFeed = (nodeId: string, alertId?: string, cameraKey?: string) => {
        const cur = feeds.get(nodeId);
        if (!cur) return;
        feeds.set(
          nodeId,
          cur.filter((a) => (alertId ? a.id !== alertId : cameraKey ? a.cameraId !== cameraKey : true)),
        );
        if (alertId) siteOfAlert.delete(alertId);
      };

      // ── Per-site link: snapshot + live socket ───────────────────────────────
      const stops = sites.map((site) => {
        const { nodeId } = site;
        let ws: WebSocket | null = null;
        let pingTimer: ReturnType<typeof setInterval> | null = null;
        let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
        let stableTimer: ReturnType<typeof setTimeout> | null = null;
        let reconnectAttempts = 0;

        // Filter to active alerts server-side (like the local node). Without status, the
        // backend returns the most-recent 100 of ALL statuses and the 'new' ones get crowded
        // out by resolved/acknowledged rows → empty feed → no counts/tiles.
        const loadSnapshot = async () => {
          try {
            const raw = await getNodeRealtimeAlerts(nodeId, { status: 'new', limit: '200' }).catch(() => null);
            if (cancelled) return;
            const list: any[] = Array.isArray(raw) ? raw : (raw as any)?.alerts || (raw as any)?.data || [];
            setFeed(nodeId, list.filter((a) => isActive(a.status)).map((a) => mapSiteAlert(nodeId, a)));
          } catch {
            /* ignore — the live socket keeps this site's slice fresh */
          }
        };
        loadSnapshot();

        const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const token = getStoredToken();
        const url = `${proto}//${window.location.host}/ws/node-events?token=${encodeURIComponent(token || '')}&nodeId=${encodeURIComponent(nodeId)}`;

        const connect = () => {
          if (cancelled) return;
          ws = new WebSocket(url);

          ws.onopen = () => {
            // Only reset the backoff once the link has proven stable; a socket that opens then
            // immediately closes (flap) must keep backing off, or it refetches every 3s.
            stableTimer = setTimeout(() => { reconnectAttempts = 0; }, RECONNECT_STABLE_MS);
            // Refetch on EVERY open, first included: the mount-time fetch can race a cold node
            // tunnel (503 / command timeout) and fail silently, and the socket opening is the
            // reliable signal that the cloud↔node path is live. Idempotent — it replaces this
            // site's slice and re-emits the merged feed.
            loadSnapshot();
            pingTimer = setInterval(() => {
              if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
            }, 15000);
          };

          ws.onmessage = (ev) => {
            let msg: any;
            try {
              msg = JSON.parse(ev.data as string);
            } catch {
              return;
            }
            const d = msg?.data || {};
            if (msg.type === 'alert:new') {
              const a = mapSiteAlert(nodeId, d);
              if (!isActive(a.status)) return;
              // Into the buffer AND out to the page: the page promotes a live tile from the
              // event, the buffer is what keeps it on the board across the next snapshot.
              addToFeed(nodeId, a);
              onAlert?.(a);
            } else if (msg.type === 'alert:preview') {
              // The node has committed the event row and its images, so the snapshot this
              // alert arrived without can now be resolved. snapshotUrl stays null — cloud has
              // no route to the node's nginx and must blob-fetch through the tunnel.
              if (rememberPreview(d.alert_id, d.source_table, d.event_id, d.image_urls)) {
                onAlertPreview?.({
                  alertId: d.alert_id,
                  cameraId: d.camera_id ? siteCameraKey(nodeId, d.camera_id) : undefined,
                  snapshotUrl: null,
                });
              }
            } else if (msg.type === 'alert:lifecycle') {
              const cameraKey = d.camera_id ? siteCameraKey(nodeId, d.camera_id) : undefined;
              // An escalation step keeps status 'new' — the alert stays in the feed.
              if (d.status !== 'new') dropFromFeed(nodeId, d.alert_id, cameraKey);
              onLifecycle?.({
                alertId: d.alert_id,
                cameraId: cameraKey as string,
                status: d.status,
                severity: toSeverity(d.severity),
                escalationLevel: d.escalation_level ?? undefined,
                escalationState: d.escalation_state ?? undefined,
                occurrences: d.occurrences ?? undefined,
              });
            } else if (msg.type === 'alerts:cleared') {
              // THIS site wiped its event store (its demo reset truncates the tables on a
              // cycle). Only its slice of the merged feed is gone, so the site's feed is
              // emptied and the whole feed re-emitted — every other site's alerts and hex
              // colours survive, which a blanket clear would have wiped. onCleared still
              // fires because the page's own card list and live tiles are cross-site and it
              // cannot tell which rows belonged to this node; setFeed's emit right after
              // repaints the counters for the sites that are still reporting.
              onCleared?.();
              setFeed(nodeId, []);
            // `demo:schedule` is deliberately NOT handled here. The banner is a statement
            // about the site you are looking at — "these alerts get wiped every N minutes" —
            // and the all-sites board is looking at all of them. One demo node among ten real
            // ones would have raised a banner over the whole estate, and even where several
            // sites are demos their cycles are independent, so any single countdown shown
            // across them would be wrong for all but one. It is reported only by the
            // single-node adapter, i.e. only once a specific site is selected.
            } else if (msg.type === 'camera:status_changed') {
              // The cloud only receives the overall camera:status_changed over the tunnel (the
              // node doesn't relay separate stream:*/recording:* up), so it drives both the
              // LIVE and REC dots together.
              if (d.camera_id && d.status !== undefined) {
                const key = siteCameraKey(nodeId, d.camera_id);
                const up = isOnline(d.status);
                onLiveStatus?.({ [key]: up });
                onRecordingStatus?.({ [key]: up });
              }
            }
          };

          ws.onclose = () => {
            if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
            if (stableTimer) { clearTimeout(stableTimer); stableTimer = null; }
            if (cancelled) return;
            // Exponential backoff with jitter, capped — a site whose socket keeps flapping
            // must not hammer its tunnel (or the other sites' share of the browser's sockets).
            const delay = Math.min(RECONNECT_BASE_MS * 2 ** reconnectAttempts, RECONNECT_MAX_MS);
            reconnectAttempts++;
            reconnectTimer = setTimeout(connect, delay * (0.8 + Math.random() * 0.4));
          };
          ws.onerror = () => {
            try { ws?.close(); } catch { /* ignore */ }
          };
        };
        connect();

        return () => {
          if (pingTimer) clearInterval(pingTimer);
          if (reconnectTimer) clearTimeout(reconnectTimer);
          if (stableTimer) clearTimeout(stableTimer);
          try { ws?.close(); } catch { /* ignore */ }
        };
      });

      return () => {
        cancelled = true;
        stops.forEach((stop) => stop());
      };
    },

    /**
     * One page of active alerts across every site.
     *
     * Each site is paged INDEPENDENTLY and the page-worth of rows is merged newest-first, so
     * an operator scrolling the panel walks every site's alerts with nothing dropped or
     * repeated. What it is not is a globally sorted cursor: page 2 can contain an alert older
     * than one further down page 1, because a site with few alerts runs out while a busy one
     * keeps going. Paging across N tunnels in true time order would mean a cross-site query
     * the cloud doesn't have.
     */
    async listActiveAlerts({ page, limit }: { page: number; limit: number }): Promise<HexAlertPage> {
      if (sites.length === 0) return { data: [], page, totalPages: 1, total: 0 };
      const perSite = await Promise.all(
        sites.map(async (site) => {
          const res: any = await getNodeAlerts(site.nodeId, {
            status: 'new',
            page,
            limit,
            sort_by: 'event_timestamp',
            sort_order: 'desc',
          }).catch(() => null);
          const rows: any[] = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
          const data = rows.filter((a) => isActive(a.status)).map((a) => mapSiteAlert(site.nodeId, a));
          const p = res?.pagination || {};
          return { data, totalPages: p.total_pages || 1, total: p.total ?? data.length };
        }),
      );

      return {
        data: perSite.flatMap((r) => r.data).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)),
        page,
        // The panel keeps scrolling while ANY site still has pages left.
        totalPages: perSite.reduce((n, r) => Math.max(n, r.totalPages), 1),
        total: perSite.reduce((n, r) => n + r.total, 0),
      };
    },

    /** The alert's snapshot, blob-fetched through ITS OWN site's tunnel. */
    async loadPreview(alert: HexAlert): Promise<string | null> {
      const nodeId = siteOf(alert);
      if (!nodeId || !alert.id) return null;
      const meta = getPreviewMeta(alert.id);
      if (!meta) return null;
      try {
        const blob: any = await getNodeModelEventImage(nodeId, meta.model, meta.eventId, meta.imageType);
        // A tunnel error (404/timeout) still resolves as a Blob (of JSON/text) under
        // responseType:'blob' — only turn real image bytes into an object URL.
        if (!blob || (blob.type && !blob.type.startsWith('image/'))) return null;
        return URL.createObjectURL(blob);
      } catch {
        return null;
      }
    },

    async acknowledgeAlert(alertId: string) {
      const nodeId = siteOfAlert.get(alertId);
      if (!nodeId) return;
      const u = currentUser();
      await acknowledgeNodeAlert(nodeId, alertId, u.id, u.name);
    },

    async resolveAlert(alertId: string, note?: string) {
      const nodeId = siteOfAlert.get(alertId);
      if (!nodeId) return;
      const u = currentUser();
      await resolveNodeAlert(nodeId, alertId, u.id, note ?? '', u.name);
    },

    siteOfAlertId: (alertId: string) => siteOfAlert.get(alertId) ?? null,
  };
}
